import Head from 'next/head';
import React from 'react';
import removeMarkdown from 'remove-markdown';
import { PostType } from 'libs/contentful/types';
import {
  renderJsonLdScript,
  Breadcrumb,
  getFaqStructuredData,
  getBreadcrumbStructuredData,
  getArticleStructuredData,
  prependUrl,
} from 'libs/universal/structuredData';
import { EXPERT_ADVICE_META_IMAGE as defaultMetaImage } from 'constants/expertAdvice';

type HeaderProps = {
  post: PostType;
  breadcrumbs: Breadcrumb[];
};

const Header: React.FC<HeaderProps> = ({ post, breadcrumbs }): JSX.Element => {
  const {
    heroCaption,
    title,
    heroImage,
    publishedAt,
    author,
    body,
    subtopic,
    topic,
    excerpt,
    tag,
    slug,
  } = post;
  const defaultMetaDescription = `Find answers to all your ${
    subtopic?.name || topic?.name || 'wedding planning'
  } questions. Our expert advice makes it simple and easy to plan an amazing wedding and future life together.`;
  const metaDescription = excerpt || defaultMetaDescription;
  const metaTitle = `${title} - Zola Expert Wedding Advice`;
  const metaImageUrl = heroImage
    ? prependUrl(`${heroImage.src}?q=75&w=1080&h=605&fit=fill`)
    : defaultMetaImage;
  const fullPathname = `https://www.zola.com/expert-advice/${slug}`;
  const formattedBody = removeMarkdown(body);
  const articleSchemaData = {
    heroImage,
    author,
    articleBody: formattedBody,
    fullPathname,
    title,
    heroCaption,
    publishedAt,
  };

  return (
    <Head>
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta
        name="keywords"
        content="Wedding, Wedding Registry, Honeymoon Fund, Wedding Planning, Guest List, Wedding Checklist, Wedding Website, Free wedding website"
      />
      <meta name="copyright" content="© 2018 Zola, Inc. All rights reserved. " />

      {/* Open Graph */}
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={metaImageUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={fullPathname} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:site_name" content="zola.com" />
      <meta property="og:phone_number" content="1-408-657-ZOLA" />
      <meta property="fb:app_id" content="215495315286955" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@zola" />
      <meta name="twitter:creator" content="@zola" />

      {/* Links */}
      <link rel="canonical" href={fullPathname} />

      {/* Schema.org */}
      {renderJsonLdScript(getArticleStructuredData(articleSchemaData))}
      {formattedBody &&
        (tag === 'Q&A' || tag === 'How To') &&
        renderJsonLdScript(getFaqStructuredData([{ question: title, answer: formattedBody }]))}
      {renderJsonLdScript(getBreadcrumbStructuredData(breadcrumbs))}
    </Head>
  );
};

export default Header;
